/**
 * LevelTestPage — Test de nivel adaptativo (audio-first).
 *
 * Port del prototipo `docs/Plan test de nivel/test-nivel-completo-v2.jsx`.
 * La lógica (motor + banco + sembrado + scoring) se mantiene íntegra en
 * `levelTestEngine.ts` y `levelTestBank.ts`. Esta página solo aporta la
 * presentación (tokens/BEM de la app, i18n, audio real) y la persistencia del
 * nivel al terminar.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Headphones,
  SpellCheck,
  BookA,
  Check,
  X,
  Sparkles,
  ChevronDown,
  ChevronUp,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Button } from '@/components/atoms/Button';
import { ProgressBar } from '@/components/atoms/ProgressBar';
import { useStoryBottomBar } from '@/context/StoryBottomBarContext';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/routes';
import { LEVEL_SEED_PREF_KEY } from '@/constants/levelSeed';
import { submitLevelTestResult } from '@/services/api/levelTest.api';
import { preferencesAPI } from '@/services/api/preferences.api';
import type { EnglishLevel } from '@/types/story.types';
import { BANK, SEED, CEFR, SESSION, type Skill } from './levelTestBank';
import {
  P3,
  pickNext,
  clampLvl,
  correctIdx,
  levelRange,
  updateTheta,
  updateSkillTheta,
  computeConfidence,
} from './levelTestEngine';
import styles from './LevelTestPage.module.scss';

type Phase = 'intro' | 'seed' | 'quiz' | 'result';

// Cuentas de prueba que pueden ver el panel de depuración "Motor" (θ/d/P).
const ENGINE_DEBUG_EMAILS = ['alice@acme.com'];

// Icono por competencia en el perfil del resultado.
const SKILL_ICON: Record<Skill, LucideIcon> = { L: Headphones, G: SpellCheck, V: BookA };

/**
 * Baraja las opciones (Fisher-Yates). En el banco la correcta está siempre la
 * primera; aquí se mezcla SOLO para mostrarlas. No afecta al cálculo: la
 * corrección se decide por el flag [texto, esCorrecta], no por la posición.
 */
function shuffleOptions(opts: [string, number][]): [string, number][] {
  const a = [...opts];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i]!;
    a[i] = a[j]!;
    a[j] = tmp;
  }
  return a;
}

export function LevelTestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  // θ de sembrado reutilizado (onboarding o test previo). Si existe, el test no
  // vuelve a preguntar las 2 can-do y arranca el motor desde este punto.
  const savedSeed = user?.preferences?.[LEVEL_SEED_PREF_KEY];
  const seededTheta = typeof savedSeed === 'number' ? clampLvl(savedSeed) : null;

  // El panel de depuración "Motor" solo se muestra a cuentas de prueba.
  const canSeeEngine = !!user?.email && ENGINE_DEBUG_EMAILS.includes(user.email.toLowerCase());

  const [phase, setPhase] = useState<Phase>('intro');
  const [seedStep, setSeedStep] = useState(0);
  const [seedVals, setSeedVals] = useState<number[]>([]);
  const [seedTheta, setSeedTheta] = useState(3.0);

  const [seen, setSeen] = useState<number[]>([]);
  const [curr, setCurr] = useState<number | null>(null);
  // Opciones de la pregunta actual, ya barajadas (estable hasta cambiar de pregunta).
  const [currOpts, setCurrOpts] = useState<[string, number][]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);
  const [count, setCount] = useState(0);
  const [theta, setTheta] = useState(3.0);
  const [sTheta, setSTheta] = useState<Record<Skill, number>>({ L: 3.0, G: 3.0, V: 3.0 });
  const [hist, setHist] = useState<number[]>([]);
  const [score, setScore] = useState(0);
  const [showEngine, setShowEngine] = useState(false);
  const [saving, setSaving] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Botones anclados en el footer (misma barra que historias/onboarding): el de
  // "Empezar test" en intro y los de "Empezar a aprender / Repetir test" en el
  // resultado.
  const bottomBar = useStoryBottomBar();
  const setBottomBarContent = bottomBar?.setContent;
  const setHeaderProgress = bottomBar?.setHeaderProgress;
  const setHideCloseButton = bottomBar?.setHideCloseButton;

  // En el resultado ya no tiene sentido la X de cerrar (se usa "Empezar a
  // aprender" / "Repetir test"); la ocultamos.
  useEffect(() => {
    setHideCloseButton?.(phase === 'result');
  }, [phase, setHideCloseButton]);
  useEffect(() => () => setHideCloseButton?.(false), [setHideCloseButton]);
  useEffect(() => {
    if (!setBottomBarContent) return;
    if (phase === 'intro') {
      setBottomBarContent(
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={() => (seededTheta !== null ? beginQuiz(seededTheta) : setPhase('seed'))}
        >
          {t('levelTest.startTest')}
        </Button>
      );
    } else if (phase === 'result') {
      setBottomBarContent(
        <>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            isLoading={saving}
            disabled={saving}
            onClick={handleStartLearning}
          >
            {saving ? t('levelTest.saving') : t('levelTest.startLearning')}
          </Button>
          <Button
            variant="ghost"
            size="lg"
            fullWidth
            disabled={saving}
            onClick={startOver}
            className={styles.levelTest__retakeBtn}
          >
            {t('levelTest.retake')}
          </Button>
        </>
      );
    } else {
      setBottomBarContent(null);
    }
    // beginQuiz/seededTheta/handleStartLearning/startOver omitidos a propósito:
    // usan setters estables y estado ya congelado en el resultado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, saving, setBottomBarContent, t]);
  useEffect(() => () => setBottomBarContent?.(null), [setBottomBarContent]);

  // Barra de progreso en la cabecera (misma que las actividades de las historias).
  useEffect(() => {
    if (!setHeaderProgress) return;
    // Endowed progress: reservamos un arranque visible (~6 %) y repartimos las
    // preguntas en el resto, para que la barra no salga vacía en la 1ª pregunta
    // pero siga avanzando en cada respuesta.
    const BASELINE = 0.06;
    const withBaseline = (answeredCount: number, total: number) =>
      total * BASELINE + answeredCount * (1 - BASELINE);
    if (phase === 'seed') {
      setHeaderProgress({ value: withBaseline(seedStep, SEED.length), max: SEED.length });
    } else if (phase === 'quiz') {
      setHeaderProgress({
        value: withBaseline(count + (answered ? 1 : 0), SESSION),
        max: SESSION,
      });
    } else {
      setHeaderProgress(null);
    }
  }, [phase, seedStep, count, answered, setHeaderProgress]);
  useEffect(() => () => setHeaderProgress?.(null), [setHeaderProgress]);

  function stopAudio() {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsAudioPlaying(false);
  }

  function beginQuiz(seedT: number) {
    const first = pickNext(seedT, [], 0);
    setPhase('quiz');
    setSeedTheta(seedT);
    setSeen(first === null ? [] : [first]);
    setCurr(first);
    setCurrOpts(first === null ? [] : shuffleOptions(BANK[first]!.opts));
    setSel(null);
    setAnswered(false);
    setCount(0);
    setTheta(seedT);
    setSTheta({ L: seedT, G: seedT, V: seedT });
    setHist([]);
    setScore(0);
    stopAudio();
  }

  function seedAnswer(v: number) {
    const vals = [...seedVals, v];
    if (seedStep + 1 < SEED.length) {
      setSeedVals(vals);
      setSeedStep(seedStep + 1);
      return;
    }
    const seedT = clampLvl(vals.reduce((s, x) => s + x, 0) / vals.length);
    setSeedTheta(seedT);
    setSeedVals(vals);
    beginQuiz(seedT);
  }

  function startOver() {
    setPhase('seed');
    setSeedStep(0);
    setSeedVals([]);
    stopAudio();
  }

  function answer(i: number) {
    if (answered || curr === null) return;
    const item = BANK[curr];
    if (!item) return;
    const ok = currOpts[i]![1] === 1;
    const n = count;
    const nt = updateTheta(theta, item.d, n, ok);
    const nps = updateSkillTheta(sTheta[item.skill], item.d, ok);
    setSel(i);
    setAnswered(true);
    setTheta(nt);
    setSTheta({ ...sTheta, [item.skill]: nps });
    setHist([...hist, nt]);
    if (ok) setScore(score + 1);
  }

  function next() {
    stopAudio();
    const newCount = count + 1;
    if (newCount >= SESSION) {
      setPhase('result');
      return;
    }
    const newSeen = [...seen];
    const nx = pickNext(theta, newSeen, newCount);
    if (nx !== null) newSeen.push(nx);
    setSeen(newSeen);
    setCurr(nx);
    setCurrOpts(nx === null ? [] : shuffleOptions(BANK[nx]!.opts));
    setSel(null);
    setAnswered(false);
    setCount(newCount);
  }

  function toggleAudio() {
    if (curr === null) return;
    const item = BANK[curr];
    if (!item || !item.audioUrl) return;
    if (isAudioPlaying) {
      stopAudio();
      return;
    }
    const el = new Audio(item.audioUrl);
    el.onended = () => setIsAudioPlaying(false);
    el.onerror = () => setIsAudioPlaying(false);
    audioRef.current = el;
    void el.play();
    setIsAudioPlaying(true);
  }

  const confidence = useMemo(() => computeConfidence(hist), [hist]);

  const skillLevels: Record<Skill, number> = {
    L: clampLvl(Math.round(sTheta.L)),
    G: clampLvl(Math.round(sTheta.G)),
    V: clampLvl(Math.round(sTheta.V)),
  };
  const mixed = Math.max(...Object.values(sTheta)) - Math.min(...Object.values(sTheta)) >= 1.0;
  // Nivel global = θ redondeada al MCER (resume las tres competencias). Es el
  // nivel que se guarda en la cuenta y el que muestra el número grande del resultado.
  const globalLevel = CEFR[clampLvl(Math.round(theta)) - 1] as EnglishLevel;

  async function handleStartLearning() {
    const placedLevel = globalLevel;
    setSaving(true);
    try {
      await submitLevelTestResult({
        level: placedLevel,
        score,
        skillProfile: {
          L: CEFR[skillLevels.L - 1] as EnglishLevel,
          G: CEFR[skillLevels.G - 1] as EnglishLevel,
          V: CEFR[skillLevels.V - 1] as EnglishLevel,
        },
      });
      // Guardamos la θ final como nuevo sembrado: un reintento del test partirá
      // de aquí y no volverá a pedir las 2 preguntas can-do.
      const merged = await preferencesAPI.patch({ [LEVEL_SEED_PREF_KEY]: theta });
      // levelTestCompletedAt: el valor autoritativo lo fija el backend en
      // /result; aquí solo marcamos la sesión para que la UI que depende de él
      // (banner del test de nivel) reaccione sin esperar a una recarga.
      updateUser({
        level: placedLevel,
        preferences: merged,
        levelTestCompletedAt: new Date().toISOString(),
      });
      navigate(ROUTES.STUDENT.DASHBOARD);
    } catch (error) {
      console.error('Failed to persist level test result:', error);
      setSaving(false);
    }
  }

  /* ============================ INTRO ============================ */
  if (phase === 'intro') {
    return (
      <div className={styles.levelTest}>
        <div className={cn(styles.levelTest__shell, styles['levelTest__shell--center'])}>
          <h1 className={styles.levelTest__heroTitle}>{t('levelTest.introTitle')}</h1>
          <p className={styles.levelTest__heroText}>{t('levelTest.introBody')}</p>
        </div>
      </div>
    );
  }

  /* ============================ SEED ============================ */
  if (phase === 'seed') {
    const sq = SEED[seedStep];
    if (!sq) return null;
    return (
      <div className={styles.levelTest}>
        <div className={styles.levelTest__shell}>
          <div className={styles.levelTest__topMeta}>
            {t('levelTest.seedStepOf', { current: seedStep + 1, total: SEED.length })}
          </div>

          <p className={styles.levelTest__seedIntro}>{t('levelTest.seedIntro')}</p>
          <h2 className={styles.levelTest__seedQuestion}>{sq.q}</h2>
          <RadioGroupPrimitive.Root
            key={seedStep}
            className={styles.levelTest__seedList}
            aria-label={sq.q}
            onValueChange={(v) => seedAnswer(sq.opts[Number(v)]![1])}
          >
            {sq.opts.map((o, i) => {
              const id = `seed-${seedStep}-${i}`;
              return (
                <label key={i} htmlFor={id} className={styles.levelTest__seedCard}>
                  <RadioGroupPrimitive.Item
                    value={String(i)}
                    id={id}
                    className={styles.levelTest__seedRadio}
                    data-no-press-scale=""
                  >
                    <RadioGroupPrimitive.Indicator className={styles.levelTest__seedIndicator} />
                  </RadioGroupPrimitive.Item>
                  <span className={styles.levelTest__seedLabel}>{o[0]}</span>
                </label>
              );
            })}
          </RadioGroupPrimitive.Root>
          <p className={styles.levelTest__footnote}>{t('levelTest.seedFooter')}</p>
        </div>
      </div>
    );
  }

  /* ============================ RESULT ============================ */
  if (phase === 'result') {
    const confLabel = t(`levelTest.confidence.${confidence}`);
    return (
      <div className={styles.levelTest}>
        <div className={styles.levelTest__shell}>
          <div className={styles.levelTest__resultHead}>
            <div className={styles.levelTest__resultLabel}>{t('levelTest.resultLabel')}</div>
            <div className={styles.levelTest__resultLevel}>{levelRange(theta)}</div>
            <p className={styles.levelTest__resultSub}>
              {t('levelTest.globalEstimate', { confidence: confLabel })} ·{' '}
              {t('levelTest.resultSummary', { score, total: SESSION })}
            </p>

            {mixed && (
              <div className={styles.levelTest__mixed}>
                <span className={styles.levelTest__mixedStrong}>
                  {t('levelTest.mixedProfile')}
                </span>{' '}
                {t('levelTest.mixedDesc', { listening: CEFR[skillLevels.L - 1], placed: globalLevel })}
              </div>
            )}
          </div>

          <div className={styles.levelTest__skills}>
            {(['L', 'G', 'V'] as Skill[]).map((sk) => {
              const lv = skillLevels[sk];
              const SkillIcon = SKILL_ICON[sk];
              return (
                <div key={sk} className={styles.levelTest__skillCard}>
                  <div className={styles.levelTest__skillHead}>
                    <span className={styles.levelTest__skillName}>
                      <SkillIcon size={15} />
                      {t(`levelTest.skills.${sk}`)}
                    </span>
                    <span className={styles.levelTest__skillLevel}>{CEFR[lv - 1]}</span>
                  </div>
                  <ProgressBar
                    value={lv}
                    max={6}
                    variant="primary"
                    aria-label={t(`levelTest.skills.${sk}`)}
                  />
                </div>
              );
            })}
          </div>

        </div>
      </div>
    );
  }

  /* ============================ QUIZ ============================ */
  if (curr === null) return null;
  const item = BANK[curr];
  if (!item) return null;
  const cIdx = correctIdx({ opts: currOpts });
  const wasCorrect = answered && sel === cIdx;

  return (
    <div className={styles.levelTest}>
      <div className={styles.levelTest__shell}>
        {canSeeEngine && (
          <div className={styles.levelTest__quizHeader}>
            <div className={styles.levelTest__quizHeaderTop}>
              <button
                type="button"
                className={styles.levelTest__engineToggle}
                onClick={() => setShowEngine((s) => !s)}
              >
                <Sparkles size={13} /> {t('levelTest.engine')}{' '}
                {showEngine ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
            </div>
            {showEngine && (
              <div className={styles.levelTest__engine}>
                <div className={styles.levelTest__engineRow}>
                  <span>{t('levelTest.engineTheta', { value: seedTheta.toFixed(1) })}</span>
                  <strong>
                    {theta.toFixed(2)} · {CEFR[clampLvl(Math.round(theta)) - 1]}
                  </strong>
                </div>
                <div className={styles.levelTest__engineRow}>
                  <span>{t('levelTest.engineDifficulty')}</span>
                  <strong>
                    {item.d.toFixed(2)} · {t(`levelTest.skills.${item.skill}`)}
                  </strong>
                </div>
                <div className={styles.levelTest__engineRow}>
                  <span>{t('levelTest.engineExpected')}</span>
                  <strong>{Math.round(P3(item.d, theta) * 100)}%</strong>
                </div>
              </div>
            )}
          </div>
        )}

        <div
          className={cn(
            styles.levelTest__quizBody,
            answered && styles['levelTest__quizBody--answered']
          )}
        >
          {item.audio ? (
            <div className={styles.levelTest__audioBlock}>
              <button
                type="button"
                className={styles.levelTest__audioBtn}
                onClick={toggleAudio}
                disabled={!item.audioUrl}
                aria-label={t('levelTest.audioBadge')}
              >
                {isAudioPlaying ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
              {!item.audioUrl && (
                <p className={styles.levelTest__audioFallback}>{item.script}</p>
              )}
              <h2 className={styles.levelTest__audioQuestion}>{item.q}</h2>
            </div>
          ) : (
            <div className={styles.levelTest__textBlock}>
              <h2 className={styles.levelTest__question}>{item.q}</h2>
            </div>
          )}

          <div className={styles.levelTest__activityOptions} role="group">
            {currOpts.map((o, i) => (
              <Button
                key={i}
                variant="secondary"
                fullWidth
                className={styles.levelTest__optionBtn}
                disabled={answered}
                onClick={() => answer(i)}
              >
                {o[0]}
              </Button>
            ))}
          </div>
        </div>

        {answered && (
          <div
            className={cn(
              styles.levelTest__feedback,
              wasCorrect
                ? styles['levelTest__feedback--correct']
                : styles['levelTest__feedback--incorrect']
            )}
          >
            <div className={styles.levelTest__feedbackBody}>
              <div className={styles.levelTest__feedbackHeader}>
                <div
                  className={cn(
                    styles.levelTest__feedbackIconCircle,
                    !wasCorrect && styles['levelTest__feedbackIconCircle--incorrect']
                  )}
                >
                  {wasCorrect ? (
                    <Check size={16} className={styles.levelTest__feedbackIconInverse} />
                  ) : (
                    <X size={16} className={styles.levelTest__feedbackIconInverse} />
                  )}
                </div>
                <span
                  className={cn(
                    styles.levelTest__feedbackTitle,
                    wasCorrect
                      ? styles['levelTest__feedbackTitle--correct']
                      : styles['levelTest__feedbackTitle--incorrect']
                  )}
                >
                  {wasCorrect ? t('levelTest.correct') : t('levelTest.incorrect')}
                </span>
              </div>
              {!wasCorrect && (
                <p className={styles.levelTest__feedbackAnswer}>
                  {t('levelTest.correctAnswerIs', { answer: currOpts[cIdx]![0] })}
                </p>
              )}
            </div>
            <Button variant={wasCorrect ? 'success' : 'error'} size="lg" fullWidth onClick={next}>
              {count + 1 >= SESSION ? t('levelTest.seeResult') : t('levelTest.next')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LevelTestPage;
